import React from "react";
//#region types
import { FormFieldProps, DateFormFieldProps } from "models/propTypes/genericFormProps";
//#endregion
//#region components
import useDebounceInput from "../hooks/useDebounceInput";
import usePlaceholder from "../hooks/usePlaceholder";
import ReadOnlyField from "../baseFields/readOnlyField";
import SimpleDatePicker from "../../datePickers/simpleDatePicker";
import { useFormikContext } from "formik";
import { FormControl } from "@mui/material";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function DatePickerFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const {
    name,
    field,
    errors,
    value,
    handleOnChangeCustom,
    handleOnBlurCustom,
    success,
    invertedDisplayType,
    optional,
    readonly,
  } = props;

  const hasError = !!errors;
  const extraProps = field.extraProps as DateFormFieldProps<T>;

  const { values } = useFormikContext<any>();

  const { internalValue, debouncedHandleOnChangeCustom } = useDebounceInput({
    name: name,
    value: value,
    handleOnChangeCustom: handleOnChangeCustom,
  });

  const placeholder = usePlaceholder(field.label, field.placeholder, optional);

  return readonly ? (
    <ReadOnlyField label={field.label} href={field.href} className={cssExports.readOnlyTextField}>
      {value ? LocalizationHelperInstance.getFormattedDate(value) : ""}
    </ReadOnlyField>
  ) : (
    <FormControl fullWidth className={cssExports.inputFormControl}>
      <SimpleDatePicker
        disabled={props.disabled}
        inputFormat="yyyy-MM-dd"
        label={LocalizationHelperInstance.getFormattedMessage(field.label)}
        overridePlaceholder={placeholder}
        value={internalValue ?? null}
        defaultCalendarMonth={extraProps?.initialFocusedDate}
        error={hasError}
        name={name}
        onChange={debouncedHandleOnChangeCustom.bind(null, name)}
        onBlur={handleOnBlurCustom.bind(null, name)}
        onFocus={field.onFocusHandler}
        minDate={
          extraProps
            ? extraProps.minDate ||
              (extraProps.minDateRefFieldName && values[extraProps.minDateRefFieldName]
                ? new Date(values[extraProps.minDateRefFieldName])
                : undefined)
            : undefined
        }
        maxDate={
          extraProps
            ? extraProps.maxDate ||
              (extraProps.maxDateRefFieldName && values[extraProps.maxDateRefFieldName]
                ? new Date(values[extraProps.maxDateRefFieldName])
                : undefined)
            : undefined
        }
        success={success}
        displayType={invertedDisplayType}
        className={field.classes?.input}
      />
    </FormControl>
  );
}

export default DatePickerFormField;
